import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  withLatestFrom,
} from 'rxjs/operators';
import { StateReducer } from './state-reducer.interface';
import { tapIfUndefined } from './tap-if-undefined.rxjs';
import { shareReplayRefCount } from './share-replay-refcount.rxjs';

export class StateService<T> {
  private memoizedObservables: Map<string, Map<string, Observable<any>>>;
  private state$: BehaviorSubject<T>;
  private updating$ = new BehaviorSubject<boolean>(false);
  protected get state(): T {
    return this.state$.getValue();
  }
  protected set state(newState: Partial<T>) {
    //setTimeout(() => {
    if (this.reducer) {
      of(newState)
        .pipe(withLatestFrom(this.state$), this.reducer.reduce())
        .subscribe((reducedState) => {
          this.updating$.next(true);
          this.state$.next({ ...this.state, ...reducedState });
          this.updating$.next(false);
        });
    } else {
      this.updating$.next(true);
      this.state$.next({ ...this.state, ...newState });
      this.updating$.next(false);
    }
    //}, 1);
  }

  constructor(initialState: T, private reducer?: StateReducer<T>) {
    this.state$ = new BehaviorSubject<T>(initialState);
    this.memoizedObservables = new Map<string, Map<string, Observable<any>>>();
  }

  protected select<K>(mapFn: (state: T) => K): Observable<K>;
  protected select<K>(
    mapFn: (state: T) => K,
    initFn?: () => void
  ): Observable<K>;
  protected select<K, A1>(
    mapFn: (state: T, a1: A1) => K,
    initFn?: () => void,
    a1?: A1
  ): Observable<K>;
  protected select<K, A1, A2>(
    mapFn: (state: T, a1: A1, a2: A2) => K,
    initFn?: () => void,
    a1?: A1,
    a2?: A2
  ): Observable<K>;
  protected select<K>(
    mapFn: (state: T, ...args: any[]) => K,
    initFn?: () => void,
    ...args: any[]
  ): Observable<K> {
    let fnCache = this.memoizedObservables.get(mapFn.toString());
    if (!fnCache) {
      fnCache = new Map<string, Observable<any>>();
      this.memoizedObservables.set(mapFn.toString(), fnCache);
    }
    let argsKey = JSON.stringify(args);
    let obs = fnCache.get(argsKey);
    if (!obs) {
      obs = combineLatest([this.state$, this.updating$]).pipe(
        filter(([state, updating]) => !updating),
        map(([state, updating]) => mapFn(state, ...args)),
        distinctUntilChanged(),
        tapIfUndefined(
          () => {
            if (typeof initFn !== 'undefined') initFn();
          },
          () => {
            fnCache!.delete(argsKey);
          }
        ),
        shareReplayRefCount()
      );
      fnCache.set(argsKey, obs);
    }
    return obs;
  }
}
